import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Cross, Sparkles, Medal, Link2, Shirt, HeartHandshake, Brain, Zap, Users, Check, PenTool, Blocks, TrendingUp } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeIn, Eyebrow, Tag } from "@/components/Motion";
import Marquee from "@/components/Marquee";
import { MASCOT_URL, IMAGES } from "@/lib/brand";

const MASCOT_TRAITS = [
    { icon: Cross, title: "The Cap & Cross", copy: "Faith over everything — grounded under the brim of a black baseball cap." },
    { icon: Sparkles, title: "The Braids", copy: "Multicolor purple tones — every lane woven into one crownless flow." },
    { icon: Medal, title: "The Gold Grill", copy: "The wins we speak into existence before they happen." },
    { icon: Link2, title: "The Silver Chains", copy: "Layered — because every connection in the Network carries weight." },
    { icon: Shirt, title: "The #5 Jersey", copy: "MY 5 energy. The number of the Network, worn like a uniform." },
];

const ETHOS = [
    { icon: HeartHandshake, title: "Human First", copy: "AI is a tool in the creative kit — never the voice. Personality and creativity stay at the center." },
    { icon: Zap, title: "Urgency & Purpose", copy: "We move like it matters, because it does. No drifting, no someday." },
    { icon: Brain, title: "Structure Over Hype", copy: "Talent is everywhere. Structure, accountability, and direction are the difference." },
    { icon: Users, title: "Community Over Competition", copy: "Your lane doesn't threaten mine. We build side by side." },
];

const STEPS = [
    { n: "STEP 1", title: "DISCOVER YOUR LANE", copy: "Tell us about your interests, goals, experience, and what you want to create." },
    { n: "STEP 2", title: "DEVELOP YOUR DIRECTION", copy: "Identify opportunities to improve your creative identity, branding, strategy, and execution." },
    { n: "STEP 3", title: "BUILD WITH PURPOSE", copy: "Use your plan to take practical steps toward your goals, with resources and support appropriate to your needs." },
];

const DIFFERENT = [
    "What makes your identity distinctive",
    "Which direction fits your interests and goals",
    "How to develop a recognizable brand",
    "What content you can create consistently",
    "Which steps to prioritize first",
    "How to use available resources without unnecessarily overspending",
];

const JOURNEY = [
    { n: "01", icon: PenTool, title: "CREATE", copy: "Raw ideas get structure. Vision boards become blueprints you can actually execute." },
    { n: "02", icon: Blocks, title: "BUILD", copy: "Brand, content, and craft — built deliberately, in a lane that belongs to you." },
    { n: "03", icon: TrendingUp, title: "GROW", copy: "Accountability, creative direction, and momentum that compounds week over week." },
    { n: "04", icon: Users, title: "CONNECT", copy: "A network of creators and builders moving with urgency and purpose — together." },
];

const MY5_CHIPS = ["Facts", "Hot Takes", "Faith", "Music", "TV & Film", "Storytimes", "Journals", "Art", "Fashion", "Gaming", "Live"];

const COMMUNITY = [
    { title: "Outreach & Support", copy: "Showing up for the community that shows up for the work." },
    { title: "Shared Resources", copy: "Toolkits, knowledge, and access passed forward — not gatekept." },
    { title: "Emergency Assistance", copy: "A future relief fund under the PURP ALERT INC. nonprofit vision.", future: true },
    { title: "Youth Support", copy: "Creative direction and mentorship for the next generation of builders." },
    { title: "Collaboration", copy: "Creators linking up across lanes to make bigger things than solo." },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <PageHero
                eyebrow="About the Network"
                titleLines={["Built from", "half a lifetime", "of entrepreneurship."]}
                copy="PURP ALERT NETWORK was founded by Indigo Alexander — an entrepreneur for more than half her life — after meeting talented person after talented person who had everything except structure, accountability, and creative direction."
            />

            {/* OFFICIAL ABOUT COPY */}
            <section className="py-16 md:py-24 border-t border-white/10" data-testid="about-official">
                <div className="max-w-3xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">About PURP ALERT NETWORK</Eyebrow>
                        <div className="space-y-5 text-slate-400 text-base leading-relaxed">
                            <p>
                                PURP ALERT NETWORK was built around a simple idea: talent deserves direction, and
                                potential deserves an opportunity to become something real.
                            </p>
                            <p>
                                We believe independent artists and creatives should have access to practical
                                guidance, creative development, and tools that help them build with intention.
                            </p>
                            <p>
                                Our focus is helping people discover their lane, develop their identity, and create
                                a path forward that makes sense for their goals.
                            </p>
                            <p>
                                Whether you&apos;re an independent artist, an emerging creator, or someone who knows
                                they have potential but hasn&apos;t figured out how to use it yet — there&apos;s room to
                                start here.
                            </p>
                            <p>
                                We&apos;re building a network where creativity meets structure, ideas meet execution,
                                and people are encouraged to become more intentional about what they create.
                            </p>
                            <p className="font-display font-extrabold uppercase text-xl md:text-2xl text-slate-100 leading-snug pt-2">
                                More than a talent agency. <span className="italic text-purp">Bigger than a record company.</span>
                            </p>
                            <p>
                                PAN is an ecosystem, not one service: music, merch, community, creative development,
                                PAN School, and P.A.R.K. — each lane feeds the others, and no single lane is the whole
                                story.
                            </p>
                            <p className="text-slate-500 text-sm">
                                It&apos;s a network built around creative potential, development, and purpose.
                            </p>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* FOUNDER STORY */}
            <section className="py-20 md:py-28 border-t border-white/10" data-testid="founder-story">
                <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
                    <FadeIn>
                        <div className="relative rounded-3xl overflow-hidden border border-purp/30 card-glow aspect-[4/5] max-w-md">
                            <img src={IMAGES.ownerSelfie} alt="Indigo Alexander — Founder of PURP ALERT NETWORK" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 to-transparent" />
                            <p className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.3em] text-purp-neon">Indigo Alexander · Founder</p>
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.15}>
                        <Eyebrow className="mb-5">The Why</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            Talent was never <span className="italic text-purp">the problem.</span>
                        </h2>
                        <div className="mt-6 space-y-5 text-slate-400 text-base leading-relaxed">
                            <p>
                                Indigo kept meeting wildly gifted people — artists, designers, storytellers, hustlers —
                                whose ideas stayed ideas. Not from lack of talent. From lack of a framework: no
                                structure, no accountability, no one giving real creative direction.
                            </p>
                            <p>
                                So she built the thing she kept wishing existed. PURP ALERT NETWORK is a creative
                                studio, community hub, entrepreneurial network, and media brand — one place where an
                                idea becomes a project, a project becomes a brand, and a brand becomes a lane.
                            </p>
                        </div>
                        <blockquote className="mt-8 border-l-2 border-purp pl-6 py-1" data-testid="founder-quote">
                            <p className="font-display italic font-bold text-2xl md:text-3xl text-slate-100 leading-snug">
                                &ldquo;I kept meeting people with everything it takes — except the structure. So I built
                                the structure.&rdquo;
                            </p>
                            <cite className="mt-3 block font-mono text-[11px] uppercase tracking-[0.3em] text-purp-neon not-italic">
                                — Indigo Alexander, Founder
                            </cite>
                        </blockquote>
                        <button
                            onClick={() => document.getElementById("the-five")?.scrollIntoView({ behavior: "smooth" })}
                            data-testid="about-five-link"
                            className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-purp-neon hover:text-purp transition-colors"
                        >
                            The meaning of #5 →
                        </button>
                    </FadeIn>
                </div>
            </section>

            {/* THE MEANING OF #5 */}
            <section id="the-five" className="py-20 md:py-28 border-t border-white/10 scroll-mt-20" data-testid="meaning-of-five">
                <div className="max-w-3xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">The Meaning of #5</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            Grace you <span className="italic text-purp">didn&apos;t earn.</span>
                        </h2>
                        <div className="mt-6 space-y-5 text-slate-400 text-base md:text-lg leading-relaxed">
                            <p>
                                The 5 represents my 5th chance at LIFE. Most people only get 1 chance to get it
                                right. I got five.
                            </p>
                            <p>
                                Biblically, the number 5 symbolizes God&apos;s grace, goodness, favor, and unearned
                                mercy toward humanity.
                            </p>
                            <p className="font-display font-extrabold uppercase text-xl md:text-2xl text-slate-100 leading-snug">
                                That&apos;s what the #5 stands for — grace you didn&apos;t earn, and a life you almost
                                didn&apos;t get to live.
                            </p>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="relative py-20 md:py-28 border-t border-white/10" data-testid="how-it-works">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn className="text-center max-w-2xl mx-auto">
                        <Eyebrow className="justify-center mb-5">How It Works</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Three steps. <span className="italic text-purp">No mystery.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-14 grid md:grid-cols-3 gap-5">
                        {STEPS.map((s, i) => (
                            <FadeIn key={s.n} delay={0.1 * i}>
                                <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-8 text-center" data-testid={`step-card-${i + 1}`}>
                                    <p className="font-mono text-[11px] tracking-[0.35em] text-volt">{s.n}</p>
                                    <h3 className="mt-3 font-display font-extrabold uppercase text-2xl md:text-3xl leading-tight">{s.title}</h3>
                                    <p className="mt-4 text-sm text-slate-400 leading-relaxed">{s.copy}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                    <FadeIn delay={0.25} className="text-center mt-10 max-w-2xl mx-auto">
                        <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                            You bring the vision. We&apos;ll help you work toward the structure, strategy, and direction
                            to move it forward.
                        </p>
                        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 leading-relaxed">
                            No guaranteed income, fame, or deals — outcomes depend on your effort, circumstances, and available opportunities.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* WHY PAN IS DIFFERENT */}
            <section className="relative py-20 md:py-28 border-t border-white/10 overflow-hidden" data-testid="why-different">
                <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
                    <FadeIn>
                        <Eyebrow className="mb-5">Why PAN</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            Creative potential deserves <span className="italic text-purp text-glow">a real plan.</span>
                        </h2>
                        <p className="mt-6 text-slate-400 text-base leading-relaxed">
                            PAN is built for people who need more than motivational speeches and generic advice. We
                            help creatives understand:
                        </p>
                        <ul className="mt-6 space-y-3">
                            {DIFFERENT.map((d) => (
                                <li key={d} className="flex items-start gap-3 text-sm md:text-base text-slate-300">
                                    <Check className="w-4 h-4 text-volt shrink-0 mt-1" /> {d}
                                </li>
                            ))}
                        </ul>
                        <blockquote className="mt-8 border-l-2 border-purp pl-6">
                            <p className="font-display italic font-bold text-xl md:text-2xl text-slate-100 leading-snug">
                                &ldquo;We&apos;re not here to sell you a dream. We&apos;re here to help you put structure
                                behind yours.&rdquo;
                            </p>
                        </blockquote>
                    </FadeIn>
                    <FadeIn delay={0.15}>
                        <div className="relative max-w-md mx-auto">
                            <div className="absolute inset-10 rounded-full bg-purp/25 blur-[100px]" />
                            <img
                                src={IMAGES.throne}
                                alt="PURP ALERT #5 — Entrepreneur, Philanthropist, Founder & CEO. I don't create content, I am the content."
                                loading="lazy"
                                className="relative w-full rounded-3xl border border-purp/30 card-glow"
                                data-testid="why-different-image"
                            />
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* THE MOVEMENT */}
            <section className="relative py-20 md:py-28 border-t border-white/10" data-testid="journey-strip">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">The Movement</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Four moves. <span className="italic text-purp">One lane.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {JOURNEY.map((j, i) => (
                            <FadeIn key={j.n} delay={0.1 * i}>
                                <motion.div
                                    whileHover={{ y: -6 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 22 }}
                                    className="group h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-7 hover:border-purp/50 transition-colors duration-300"
                                    data-testid={`journey-card-${j.title.toLowerCase()}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <j.icon className="w-6 h-6 text-purp-neon" />
                                        <span className="font-mono text-xs text-slate-500">{j.n}</span>
                                    </div>
                                    <h3 className="mt-16 font-display font-extrabold uppercase text-3xl group-hover:text-purp-neon transition-colors duration-300">
                                        {j.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{j.copy}</p>
                                </motion.div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* SIGNATURE CONTENT */}
            <section className="relative py-20 md:py-28 border-t border-white/10" data-testid="my5-teaser">
                <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
                    <FadeIn>
                        <Eyebrow className="mb-5">Signature Content</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.92]">
                            Not Top 5.<br />
                            <span className="italic text-purp text-glow">MY 5.</span>
                        </h2>
                        <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed max-w-lg">
                            Facts, hot takes, faith, music reviews, TV &amp; movies, storytimes, journaling, art,
                            fashion, gaming, live — whatever five the creator feels like sharing that day. Your five.
                            Your way. Every time.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-2">
                            {MY5_CHIPS.map((c) => (
                                <Tag key={c}>{c}</Tag>
                            ))}
                        </div>
                        <Link
                            to="/my-5"
                            data-testid="my5-teaser-cta"
                            className="mt-9 inline-flex items-center gap-2 rounded-full bg-purp px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(168,85,247,0.65)]"
                        >
                            Enter MY 5 <ArrowRight className="w-4 h-4" />
                        </Link>
                    </FadeIn>
                    <FadeIn delay={0.15}>
                        <motion.div
                            whileHover={{ rotate: 0, scale: 1.02 }}
                            initial={false}
                            className="rounded-2xl bg-panel/75 backdrop-blur-md border border-purp/35 card-glow p-8 rotate-2"
                            data-testid="my5-teaser-card"
                        >
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-volt">Sample Drop</p>
                            <h3 className="mt-2 font-display font-extrabold uppercase text-4xl">MY 5: Hot Takes</h3>
                            <ol className="mt-6 space-y-4">
                                {[
                                    "\"Exposure\" never paid a bill.",
                                    "Talent without structure is a hobby.",
                                    "AI is a tool — the taste is human.",
                                    "You don't need permission to build.",
                                    "Done beats perfect. Every time.",
                                ].map((t, i) => (
                                    <li key={i} className="flex gap-4 items-baseline">
                                        <span className="font-display font-extrabold text-purp text-xl">{i + 1}</span>
                                        <span className="text-slate-300 text-sm md:text-base">{t}</span>
                                    </li>
                                ))}
                            </ol>
                        </motion.div>
                    </FadeIn>
                </div>
            </section>

            {/* COMMUNITY FIRST */}
            <section className="relative py-20 md:py-28 border-t border-white/10" data-testid="community-teaser">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn className="max-w-2xl">
                        <Eyebrow className="mb-5">Community First</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Built for people <span className="italic text-purp">willing to do the work.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
                        {COMMUNITY.map((c, i) => (
                            <FadeIn key={c.title} delay={0.08 * i}>
                                <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-6 hover:border-purp/50 transition-colors duration-300" data-testid={`community-card-${i}`}>
                                    {c.future && <Tag tone="volt">Future</Tag>}
                                    <h3 className="mt-3 font-display font-bold uppercase text-xl leading-tight">{c.title}</h3>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.copy}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                    <FadeIn delay={0.2}>
                        <Link to="/community" className="mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-purp-neon hover:text-purp transition-colors" data-testid="community-teaser-link">
                            See the full vision <ArrowRight className="w-4 h-4" />
                        </Link>
                    </FadeIn>
                </div>
            </section>

            <Marquee items={["Human First", "Urgency & Purpose", "Structure Over Hype", "Community Over Competition"]} />

            {/* MASCOT BREAKDOWN */}
            <section className="py-20 md:py-28" data-testid="mascot-breakdown">
                <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
                    <FadeIn className="order-2 lg:order-1">
                        <Eyebrow className="mb-5">The Mascot</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            Every detail <span className="italic text-purp">means something.</span>
                        </h2>
                        <div className="mt-10 space-y-6">
                            {MASCOT_TRAITS.map((t, i) => (
                                <FadeIn key={t.title} delay={0.07 * i}>
                                    <div className="flex gap-5 items-start" data-testid={`mascot-trait-${i}`}>
                                        <span className="shrink-0 w-11 h-11 rounded-xl bg-panel border border-purp/40 flex items-center justify-center">
                                            <t.icon className="w-5 h-5 text-purp-neon" />
                                        </span>
                                        <div>
                                            <h3 className="font-display font-bold uppercase text-xl">{t.title}</h3>
                                            <p className="mt-1 text-sm text-slate-400 leading-relaxed">{t.copy}</p>
                                        </div>
                                    </div>
                                </FadeIn>
                            ))}
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.15} className="order-1 lg:order-2">
                        <div className="relative max-w-md mx-auto">
                            <div className="absolute inset-10 rounded-full bg-purp/25 blur-[100px]" />
                            <img
                                src={MASCOT_URL}
                                alt="PURP ALERT mascot — purple female-presenting cat with black cross cap, purple-toned braids, gold grill, silver chains, #5 jersey"
                                loading="lazy"
                                className="relative w-full rounded-3xl border border-purp/30 card-glow"
                                data-testid="about-mascot-image"
                            />
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* LLC VS INC */}
            <section className="py-20 md:py-28 border-t border-white/10" data-testid="structure-clarity">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn className="max-w-2xl">
                        <Eyebrow className="mb-5">Straight Up</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            Two names. <span className="italic text-purp">One honest roadmap.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-14 grid md:grid-cols-2 gap-5">
                        <FadeIn>
                            <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-purp/40 card-glow p-8" data-testid="structure-now-card">
                                <Tag>Now</Tag>
                                <h3 className="mt-4 font-display font-extrabold uppercase text-3xl">PURP ALERT NETWORK</h3>
                                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mt-1">PAN LLC — active &amp; building</p>
                                <p className="mt-5 text-slate-400 text-sm md:text-base leading-relaxed">
                                    The business being built today: the creative studio, the community hub, the media
                                    brand, the MY 5 content engine, and the merch that funds the mission. This is the
                                    Network you are looking at right now.
                                </p>
                            </div>
                        </FadeIn>
                        <FadeIn delay={0.12}>
                            <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-8" data-testid="structure-future-card">
                                <Tag tone="volt">Future</Tag>
                                <h3 className="mt-4 font-display font-extrabold uppercase text-3xl">PURP ALERT INC.</h3>
                                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mt-1">A separate nonprofit vision</p>
                                <p className="mt-5 text-slate-400 text-sm md:text-base leading-relaxed">
                                    The future nonprofit organization for outreach, emergency assistance, and youth
                                    support. To be clear: PURP ALERT INC. is not yet formed and is not a 501(c)(3).
                                    It is a promise we are building toward — and we will say so until it is real.
                                </p>
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* ETHOS */}
            <section className="py-20 md:py-28 border-t border-white/10" data-testid="ethos-section">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">The Ethos</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            What we <span className="italic text-purp">stand on.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {ETHOS.map((e, i) => (
                            <FadeIn key={e.title} delay={0.08 * i}>
                                <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-7 hover:border-purp/50 transition-colors duration-300" data-testid={`ethos-card-${i}`}>
                                    <e.icon className="w-6 h-6 text-purp-neon" />
                                    <h3 className="mt-6 font-display font-bold uppercase text-2xl">{e.title}</h3>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{e.copy}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                    <FadeIn delay={0.2}>
                        <Link to="/get-involved" className="mt-12 inline-flex items-center gap-2 rounded-full bg-purp px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(168,85,247,0.65)]" data-testid="about-get-involved-cta">
                            Join the Network <ArrowRight className="w-4 h-4" />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
}
